import type { FC } from 'react';
import { Fragment, useMemo } from 'react';
import { range } from 'lodash-es';

import { MIN_PAGES_WITHOUT_PAGINATION } from '../constants';
import { getPaginationItems } from '../utils';

import { PaginationItem } from './PaginationItem';

interface PaginationItemsProps {
  currentPageIndex: number;
  onPageChange: (newPageIndex: number) => void;
  totalPages: number;
}

const renderDots = () => (
  <span
    className="border-info-300 text-info-700 relative inline-flex items-center border bg-white px-4 py-2 text-sm font-medium"
    key="pagination-dots"
  >
    ...
  </span>
);

export const PaginationItems: FC<PaginationItemsProps> = ({ currentPageIndex, onPageChange, totalPages }) => {
  const paginationItems = useMemo(
    () => getPaginationItems(currentPageIndex, totalPages),
    [currentPageIndex, totalPages],
  );

  if (totalPages <= MIN_PAGES_WITHOUT_PAGINATION) {
    return range(1, totalPages + 1).map((i, idx, arr) => (
      <PaginationItem
        currentPageIndex={currentPageIndex}
        key={`page-number-${i}`}
        onClick={onPageChange}
        page={i}
        {...(idx === arr.length - 1 ? { 'data-testid': 'last-page-pagination' } : {})}
      />
    ));
  }

  return paginationItems.map((item, idx, arr) =>
    typeof item === 'number' ? (
      <PaginationItem
        currentPageIndex={currentPageIndex}
        key={`page-number-${item}`}
        onClick={onPageChange}
        page={item}
        {...(idx === arr.length - 1 ? { 'data-testid': 'last-page-pagination' } : {})}
      />
    ) : (
      <Fragment key={`dot-${idx}`}>{renderDots()}</Fragment>
    ),
  );
};
